import { useState } from 'react';

import { Anchor, Badge, Button, Card, Group, Stack, Text, TextInput } from '@mantine/core';
import {
  IconBrandFacebook,
  IconBrandInstagram,
  IconBrandLinkedin,
  IconBrandPinterest,
  IconBrandReddit,
  IconBrandTiktok,
  IconBrandX,
  IconBrandYoutube,
  IconChevronDown,
  IconChevronUp,
  IconWorld,
} from '@tabler/icons-react';

import { safeExternalUrl } from '@/types/monitoring';
import type {
  FinderEvidenceEntry,
  FinderProfileEvidence,
  OsintLinkedProfile,
  ProfileDecision,
  ProfileEvidence,
  ProfileQueueReason,
  ProfileSource,
  ProfileStatus,
  SubjectProfile,
} from '@/types/profile-review';
import { ReviewVerdictControls, type ReviewVerdict } from '../ReviewVerdict';
import classes from './ProfileReviewCard.module.css';

export interface ProfileReviewCardProps {
  profile: SubjectProfile;
  /** Verdict buttons are inert while a mutation is in flight. A row that is
   * no longer pending shows no buttons at all. */
  disabled?: boolean;
  /** Also show the finder's raw rule code under its plain-language reason
   * (admins: the code is what the finder's docs and logs speak). Clients
   * never see it. */
  showSystemDetail?: boolean;
  onDecide: (decision: ProfileDecision, note?: string | null) => void | Promise<void>;
}

const COLLAPSE_EVIDENCE_OVER = 3;

/** The verdict pair as the reviewer sees it; the values are the API's. */
const REJECT_VERDICT: ReviewVerdict<ProfileDecision> = {
  value: 'reject',
  label: 'Reject profile',
  testId: 'reject-profile',
};
const CONFIRM_VERDICT: ReviewVerdict<ProfileDecision> = {
  value: 'confirm',
  label: 'Confirm profile',
  testId: 'confirm-profile',
};

/** The finder's rule codes (`R<n> <token>[: detail]`, vNext-api
 * `profile_finder/rules.py`) in the reviewer's language, keyed by token.
 * Candidate verdicts reach the queue, so most entries explain why the
 * profile could not be confirmed; one confirmed verdict is queued too:
 * `confirmed_unmerged` (a confirmation the order did not take, vNext-api
 * `profile_review_service.CONFIRMED_UNMERGED_REASON`).
 * A code outside the map falls back to a generic line rather than leaking
 * the rule set. */
const FINDER_REASON_LABELS: Record<string, string> = {
  name_only:
    'Only the name was found on the page; no employer, role or place ties it to the subject',
  no_organisation:
    'The name and a role or place matched, but no organisation the subject belongs to',
  crowded: 'A common name with too little on the page to rule out a namesake',
  not_profile_page: 'Found on a post or page from this account, not on the profile itself',
  name_in_content_only: "The name appears in the page text but is not the profile's own name",
  ambiguous: 'More than one profile on this platform fits the subject equally well',
  uncorroborated:
    'Only unverified signals point to this account; nothing on the page confirms them',
  lifted_handle:
    'A handle reused from another platform; the account exists but nothing ties it to the subject',
  name_conflict: 'The page names a different person',
  confirmed_unmerged:
    "The subject's name and an organisation they belong to were both found on the page; confirm to include it in the screening",
};

const FINDER_REASON_FALLBACK = 'The system could not confirm this profile';

const FINDER_CODE = /^R\d+\s+(?<token>[a-z_]+)(?::\s*(?<detail>.*))?$/i;

/** A reason the card has reworded for the reviewer, with the system's own
 * text kept for the admin detail line (null when there is none). */
interface ReviewerReason {
  text: string;
  code: string | null;
}

/** A finder row's `system_reason` as the reviewer reads it. Null when the
 * reason is not a rule code (prose is shown verbatim). */
function finderReason(reason: string): ReviewerReason | null {
  const match = FINDER_CODE.exec(reason.trim());
  if (!match?.groups) {
    return null;
  }
  const { token, detail } = match.groups;
  const label = FINDER_REASON_LABELS[token.toLowerCase()];
  if (!label) {
    return { text: FINDER_REASON_FALLBACK, code: reason };
  }
  // An unmerged confirmation names the rule that confirmed it as its
  // detail (`R8 confirmed_unmerged: R2 name_only`); that rule's own sentence
  // describes the evidence better than the generic one, so it is shown
  // instead when the card knows it.
  if (token.toLowerCase() === 'confirmed_unmerged' && detail) {
    const confirming = FINDER_CODE.exec(detail.trim());
    const confirmingLabel =
      confirming?.groups && FINDER_REASON_LABELS[confirming.groups.token.toLowerCase()];
    if (confirmingLabel) {
      return { text: confirmingLabel, code: reason };
    }
  }
  return { text: detail ? `${label} (${detail.trim()})` : label, code: reason };
}

/** The account lookup's queue reasons in the reviewer's language. The
 * `system_reason` on these rows is written for the log ("not the supplied
 * profile with this handle; it carries the subject's name but is neither
 * verified nor linked to or from a given profile"), so the sentence is
 * keyed on the queue reason and the raw text goes to the admin detail
 * line, as a finder code does. */
const OSINT_REASON_LABELS: Partial<Record<ProfileQueueReason, string>> = {
  osint_unvouched_name:
    "An account under the subject's name that the platform has not verified and that none of the subject's known profiles link to or from",
  osint_other_name_contact:
    "Found through the subject's contact details, but the account carries a different person's name",
};

/** The reason as the reviewer reads it: a finder rule code or an account
 * lookup's queue reason mapped to a sentence. Null when `system_reason` is
 * shown verbatim (every other source, a finder reason that is prose, an
 * account-lookup row queued for another reason). */
function reviewerReason(profile: SubjectProfile): ReviewerReason | null {
  if (profile.source === 'profile-finder') {
    return profile.systemReason ? finderReason(profile.systemReason) : null;
  }
  if (profile.source === 'osint-industries') {
    const text = OSINT_REASON_LABELS[profile.queueReason];
    return text ? { text, code: profile.systemReason } : null;
  }
  return null;
}

/** Ferretly platform slugs with a brand icon; anything else renders the
 * slug plainly under a globe. */
const PLATFORMS: Record<string, { label: string; Icon: typeof IconWorld }> = {
  linkedin: { label: 'LinkedIn', Icon: IconBrandLinkedin },
  twitter: { label: 'X', Icon: IconBrandX },
  facebook: { label: 'Facebook', Icon: IconBrandFacebook },
  instagram: { label: 'Instagram', Icon: IconBrandInstagram },
  tiktok: { label: 'TikTok', Icon: IconBrandTiktok },
  youtube: { label: 'YouTube', Icon: IconBrandYoutube },
  reddit: { label: 'Reddit', Icon: IconBrandReddit },
  pinterest: { label: 'Pinterest', Icon: IconBrandPinterest },
};

/** Outcomes, not tool names: the requesting team reads these badges and
 * is introduced to neither the profile finder nor the OSINT vendor. */
const SOURCE_LABELS: Record<ProfileSource, string> = {
  client: 'Given by client',
  analyst: 'Given by analyst',
  research: 'Found in research',
  'osint-industries': 'Found by account lookup',
  'profile-finder': 'Found by the system',
};

const REASON_CHIPS: Record<ProfileQueueReason, { label: string; color: string }> = {
  system_ordered: { label: 'Would be screened as is', color: 'blue' },
  finder_candidate: { label: 'Possible match', color: 'orange' },
  osint_unvouched_name: { label: 'Name matches, not verified', color: 'yellow' },
  osint_other_name_contact: {
    label: 'Found by contact details under another name',
    color: 'violet',
  },
  reviewer_added: { label: 'Added by reviewer', color: 'teal' },
};

const STATUS_CHIPS: Record<Exclude<ProfileStatus, 'pending'>, { label: string; color: string }> = {
  confirmed: { label: 'Confirmed', color: 'green' },
  rejected: { label: 'Rejected', color: 'red' },
  superseded: { label: 'Superseded', color: 'gray' },
};

function formatPercent(value: number): string {
  return `${Math.round(Math.max(0, Math.min(1, value)) * 100)}%`;
}

/** The URL as the reviewer reads it: host and path, without the scheme,
 * query or trailing slash. */
function displayUrl(href: string): string {
  const url = new URL(href);
  return `${url.hostname}${url.pathname.replace(/\/$/, '')}`;
}

function evidenceKindLabel(kind: string): string {
  const words = kind.replace(/[_-]+/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** A finder row's evidence is a list of signals; anything that is not an
 * object with a string `kind` is dropped rather than rendered as JSON. */
function finderEvidence(evidence: ProfileEvidence): FinderProfileEvidence | null {
  const entries = evidence.evidence;
  if (!Array.isArray(entries)) {
    return null;
  }
  const known: FinderEvidenceEntry[] = [];
  for (const entry of entries) {
    if (
      entry &&
      typeof entry === 'object' &&
      typeof (entry as { kind?: unknown }).kind === 'string'
    ) {
      known.push(entry as FinderEvidenceEntry);
    }
  }
  return known.length > 0 ? { evidence: known } : null;
}

/** A linked profile as one line: the URL when there is one, else
 * `platform @username`; a bare string is taken as the URL. Null for
 * anything else, so an unexpected shape is dropped rather than printed. */
function linkedProfileText(entry: unknown): string | null {
  if (typeof entry === 'string') {
    return entry.trim() || null;
  }
  if (!entry || typeof entry !== 'object') {
    return null;
  }
  const { profile_url, platform, username } = entry as OsintLinkedProfile;
  if (typeof profile_url === 'string' && profile_url.trim()) {
    return profile_url.trim();
  }
  const handle = typeof username === 'string' && username.trim() ? `@${username.trim()}` : '';
  const site = typeof platform === 'string' ? platform.trim() : '';
  return [site, handle].filter(Boolean).join(' ') || null;
}

/** `OsintProfileEvidence` reduced to the lines the card shows. */
interface OsintSummary {
  name: string | null;
  verified: boolean;
  linked: string[];
}

/** An OSINT row's evidence describes the account; the selector value itself
 * (an email address or phone number) never reaches the frontend. */
function osintEvidence(evidence: ProfileEvidence): OsintSummary | null {
  const name = typeof evidence.name_on_account === 'string' ? evidence.name_on_account : null;
  const verified = evidence.verified === true;
  const linked = Array.isArray(evidence.linked_profiles)
    ? evidence.linked_profiles.map(linkedProfileText).filter((p): p is string => p !== null)
    : [];
  if (!name && !verified && linked.length === 0) {
    return null;
  }
  return { name, verified, linked };
}

const ARCHIVE_MONTH = new Intl.DateTimeFormat('en-GB', {
  month: 'long',
  year: 'numeric',
  timeZone: 'UTC',
});
const ARCHIVE_COUNT = new Intl.NumberFormat('en-GB');

/** An `archive_history` entry (the web archive's record of the profile URL:
 * `first_capture`, `last_capture`, `captures`) as one line. Null when the
 * first capture is not a readable date although captures exist, so the
 * entry falls back to the generic rendering rather than misstating it. */
function archiveHistoryLine(entry: FinderEvidenceEntry): string | null {
  const captures = typeof entry.captures === 'number' ? entry.captures : 0;
  if (captures <= 0 || typeof entry.first_capture !== 'string') {
    return 'Not in the web archive';
  }
  const first = new Date(entry.first_capture);
  if (Number.isNaN(first.getTime())) {
    return null;
  }
  const count = `${ARCHIVE_COUNT.format(captures)} ${captures === 1 ? 'capture' : 'captures'}`;
  return `First seen in the web archive: ${ARCHIVE_MONTH.format(first)} (${count})`;
}

function FinderEvidenceItem({ entry }: Readonly<{ entry: FinderEvidenceEntry }>) {
  const archive = entry.kind === 'archive_history' ? archiveHistoryLine(entry) : null;
  if (archive) {
    return (
      <div className={classes.evidenceEntry}>
        <Text size="xs">{archive}</Text>
      </div>
    );
  }
  const url = typeof entry.url === 'string' ? safeExternalUrl(entry.url) : null;
  const snippet = typeof entry.snippet === 'string' ? entry.snippet : null;
  return (
    <div className={classes.evidenceEntry}>
      <Text size="xs" fw={500}>
        {evidenceKindLabel(entry.kind)}
      </Text>
      {snippet && (
        <Text size="xs" c="dimmed" className={classes.snippet}>
          {snippet}
        </Text>
      )}
      {url && (
        <Anchor
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          size="xs"
          className={classes.url}
        >
          {displayUrl(url)}
        </Anchor>
      )}
    </div>
  );
}

function FinderEvidenceList({ entries }: Readonly<{ entries: FinderEvidenceEntry[] }>) {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? entries : entries.slice(0, COLLAPSE_EVIDENCE_OVER);

  return (
    <Stack gap={4}>
      {visible.map((entry, index) => (
        <FinderEvidenceItem key={`${entry.kind}-${index}`} entry={entry} />
      ))}
      {entries.length > COLLAPSE_EVIDENCE_OVER && (
        <Button
          variant="subtle"
          size="compact-xs"
          onClick={() => setExpanded((v) => !v)}
          leftSection={expanded ? <IconChevronUp size={12} /> : <IconChevronDown size={12} />}
        >
          {expanded ? 'Show fewer' : `Show all ${entries.length} signals`}
        </Button>
      )}
    </Stack>
  );
}

function OsintEvidenceSummary({ evidence }: Readonly<{ evidence: OsintSummary }>) {
  return (
    <Stack gap={2}>
      {evidence.name && <Text size="xs">Name on the account: {evidence.name}</Text>}
      {evidence.verified && <Text size="xs">Verified by the platform</Text>}
      {evidence.linked.length > 0 && (
        <Text size="xs">Linked profiles: {evidence.linked.join(', ')}</Text>
      )}
    </Stack>
  );
}

/** One queued social media profile: where it points, why the system could
 * not vouch for it, and the reviewer's verdict. The verdict controls sit in
 * the header so a card can be decided without scrolling past its detail. */
export function ProfileReviewCard({
  profile,
  disabled = false,
  showSystemDetail = false,
  onDecide,
}: Readonly<ProfileReviewCardProps>) {
  const [note, setNote] = useState(profile.note ?? '');

  const { label: platformLabel, Icon: PlatformIcon } = PLATFORMS[profile.platform] ?? {
    label: profile.platform,
    Icon: IconWorld,
  };
  const reason = REASON_CHIPS[profile.queueReason] ?? {
    label: profile.queueReason.replace(/_/g, ' '),
    color: 'gray',
  };
  const sourceLabel = SOURCE_LABELS[profile.source] ?? profile.source;
  const statusChip = profile.status === 'pending' ? null : STATUS_CHIPS[profile.status];
  const settled = profile.status !== 'pending';
  const inert = disabled || settled;
  const href = safeExternalUrl(profile.profileUrl);
  const finder = finderEvidence(profile.evidence);
  const osint = finder ? null : osintEvidence(profile.evidence);
  // Finder rows carry a rule code and account-lookup rows a log sentence
  // as their reason; both are reworded for the reviewer. Every other
  // source writes prose for the queue and is shown verbatim.
  const mappedReason = reviewerReason(profile);
  const reasonText = mappedReason?.text ?? profile.systemReason;
  const hasReason = Boolean(reasonText) || profile.confidence !== null;
  const systemDetail = showSystemDetail ? mappedReason?.code : null;

  const decide = (decision: ProfileDecision) => onDecide(decision, note.trim() || null);

  return (
    <Card
      withBorder
      padding="sm"
      className={classes.card}
      data-decision={profile.decision ?? undefined}
      data-settled={settled ? '' : undefined}
      data-testid={`profile-review-card-${profile.id}`}
    >
      <Stack gap="xs">
        <Group justify="space-between" wrap="nowrap" align="flex-start" gap="xs">
          <Group gap="xs" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
            <PlatformIcon size={20} aria-hidden />
            <div style={{ minWidth: 0 }}>
              <Text fw={600} className={classes.platform}>
                {platformLabel}
              </Text>
              <Text size="xs" c="dimmed">
                {profile.username ? `@${profile.username.replace(/^@/, '')}` : 'no handle'}
              </Text>
            </div>
          </Group>
          {!settled && (
            <ReviewVerdictControls
              reject={REJECT_VERDICT}
              confirm={CONFIRM_VERDICT}
              decision={profile.decision}
              disabled={inert}
              onDecide={decide}
            />
          )}
        </Group>

        {href ? (
          <Anchor
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            size="sm"
            className={classes.url}
          >
            {displayUrl(href)}
          </Anchor>
        ) : (
          <Text size="sm" c="dimmed" className={classes.url}>
            {profile.profileUrl}
          </Text>
        )}

        <Group gap="xs" wrap="wrap">
          <Badge variant="outline" color="gray" size="sm">
            {sourceLabel}
          </Badge>
          <Badge variant="light" color={reason.color} size="sm">
            {reason.label}
          </Badge>
          {profile.late && (
            <Badge variant="light" color="grape" size="sm">
              Recorded late
            </Badge>
          )}
          {statusChip && (
            <Badge variant="filled" color={statusChip.color} size="sm">
              {statusChip.label}
            </Badge>
          )}
        </Group>

        {hasReason && (
          <div>
            <Text size="xs" fw={600} c="dimmed" tt="uppercase">
              Why it&apos;s here
            </Text>
            {reasonText && (
              <Text size="sm" className={classes.reason}>
                {reasonText}
              </Text>
            )}
            {systemDetail && (
              <Text size="xs" c="dimmed" ff="monospace" data-testid="profile-system-detail">
                {systemDetail}
              </Text>
            )}
            {profile.confidence !== null && (
              <Text size="xs" c="dimmed">
                Confidence {formatPercent(profile.confidence)}
              </Text>
            )}
          </div>
        )}

        {(finder || osint) && (
          <div className={classes.evidence}>
            <Text size="xs" fw={600} c="dimmed" tt="uppercase" mb={4}>
              Evidence
            </Text>
            {finder && <FinderEvidenceList entries={finder.evidence} />}
            {osint && <OsintEvidenceSummary evidence={osint} />}
          </div>
        )}

        <TextInput
          size="xs"
          placeholder="Note (optional)"
          value={note}
          onChange={(e) => setNote(e.currentTarget.value)}
          disabled={inert}
          aria-label="Review note"
        />
      </Stack>
    </Card>
  );
}
